/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { FooterSection, Footer, FooterLinkList } from '@govhhs/govhhs-design-system-react';
import { RouterLink } from '@spm/core-ui';
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import LanguageDropdown from '../LanguageDropdown';
import ApplicationFooterMessages from './ApplicationFooterMessages';
import paths from '../../paths';
import { ID } from './constants';
// import LanguageDropdown from '../LanguageDropdown';
/**
 * The ApplicationFooter component handles the rendering of the Application Footer
 * feature incorporating the {@link LanguageDropdown} component to manage the local selection.
 *
 * ACES Customization:  Grey out FAQ and remove Security and Accessibility.  Privacy, Contact Us,
 * and Disclaimer routes to custom components.
 *
 * @param intl The API to format dates, numbers and strings.
 * @param location Locations represent where the app is now, where you want it to go
 * and where it was.
 *
 * @namespace ApplicationFooter
 * @memberof features.ApplicationFooter
 */
const ApplicationFooter = ({ intl, location }) => {
  const isApplicationOrSubmssionForm =
    new RegExp(`${PATHS.APPLY}/-?\\d+`).test(location.pathname) &&
    !/success/.test(location.pathname);

  // while Activate citizen account flow.
  const isCitizenAccountActivation = /activate-account/.test(location.pathname);
  if (isCitizenAccountActivation) {
    return null;
  }

  if ([PATHS.LOGIN, PATHS.SIGNUP].includes(location.pathname)) {
    return null;
  }
  if (isApplicationOrSubmssionForm) {
    return (
      <Footer type="small">
        <FooterSection
          category="secondary"
          languageDropdown={<LanguageDropdown />}
          languageDropdownWidth="1/6"
        >
          <FooterLinkList listItemsWidth="1/4" listSectionWidth="5/6" />
        </FooterSection>
      </Footer>
    );
  }
  /* START CCCS 281 - language toogle removing from  footer in the application submission page. */
  const successpage = new RegExp(`${PATHS.APPLY}/-?\\d+`).test(location.pathname) &&
  /success/.test(location.pathname); 
 if(successpage) {
  return (
    <Footer type="small">
      <FooterSection
        ariaLabel={intl.formatMessage(ApplicationFooterMessages.footerNavigation)}
        category="secondary"
      >
        <FooterLinkList listItemsWidth="1/4" listSectionWidth="5/6">
          <span>{intl.formatMessage(ApplicationFooterMessages.footerFAQ)}</span>
          <RouterLink data-testid={ID.CONTACT_US_FOOTER_ID} to={PATHS.CONTACT_US} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerContactUs)}
          </RouterLink>
          <RouterLink data-testid={ID.PRIVACY_FOOTER_ID} to={paths.PRIVACY} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerPrivacy)}
          </RouterLink>
          <RouterLink data-testid={ID.DISCLAIMER_FOOTER_ID} to={paths.DISCLAIMER} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerDisclaimer)}
          </RouterLink>
        </FooterLinkList>
      </FooterSection>
    </Footer>
  );
}
/* END CCCS 281 - language toogle removing from  footer in the application submission page. */
  return (
    <Footer type="small">
      <FooterSection
        ariaLabel={intl.formatMessage(ApplicationFooterMessages.footerNavigation)}
        category="secondary"
        languageDropdown={<LanguageDropdown />}
        languageDropdownWidth="1/6"
      >
        <FooterLinkList listItemsWidth="1/4" listSectionWidth="5/6">
          <span>{intl.formatMessage(ApplicationFooterMessages.footerFAQ)}</span>
          <RouterLink data-testid={ID.CONTACT_US_FOOTER_ID} to={PATHS.CONTACT_US} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerContactUs)}
          </RouterLink>
          <RouterLink data-testid={ID.PRIVACY_FOOTER_ID} to={paths.PRIVACY} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerPrivacy)}
          </RouterLink>
          <RouterLink data-testid={ID.DISCLAIMER_FOOTER_ID} to={paths.DISCLAIMER} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerDisclaimer)}
          </RouterLink>
        </FooterLinkList>
      </FooterSection>
    </Footer>
  );
};

ApplicationFooter.propTypes = {
  intl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

/**
 * Controls the rendering of the Application Footer component incorporating the
 * {@link LanguageDropdown} component to manage the locale selection.
 * @namespace
 */
export default withRouter(injectIntl(ApplicationFooter));
